// @flow

import React from "react";
import classNames from "classnames";
import { DragWrapper, DropWrapper, withDragDropContext } from "../drag-and-drop";
import "./base-hierarchy-tree.style.scss";
import { ContextMenu, MenuItem, View, Text, List } from "@components";

export type TreeLeaf = $Exact<{
  id: number,
  employeeId?: number | null,
  type: string,
  path: string,
  parentId?: number,
  label: string,
  reference: string,
  numEmployees: number | null,
  accessible: boolean | null,
  isActive: boolean,
  showLeavers: boolean,
}>;

export type TreeBranch = $Exact<{
  data: TreeLeaf,
  children: Array<TreeBranch>,
}>;

type Props = $Exact<{
  className: string,
  tree: TreeBranch,
  draggable: boolean,
  expanded: number[],
  excluded: number[],
  disabled: boolean,
  hasControl: boolean,
  expandAll: boolean,
  hoverEmployee: boolean,
  hideEmployee: boolean,
  showDisabled: boolean,
  selectedBranchId: number | null,
  actions: (branch: TreeBranch) => React$Node,
  onDrop: (from: number, to: number) => void,
  onClick: (branch: TreeBranch) => void,
  renderControl: (branch: TreeBranch, isHovered: boolean, isInactive: boolean) => React$Node,
  showEmployees: boolean,
}>;

type State = $Exact<{
  hoveredNode: number | null,
  expanded: number[],
  expandedFromParent: number[],
}>;

export class BaseHierarchyTree extends React.Component<Props, State> {
  static defaultProps = {
    expanded: [],
    excluded: [],
    expandAll: false,
    hasControl: false,
    draggable: false,
    onClick: () => undefined,
    renderControl: () => <div />,
  };

  constructor(props: Props) {
    super(props);

    this.state = {
      expanded: [],
      hoveredNode: null,
      expandedFromParent: props.expanded,
    };
  }

  static getDerivedStateFromProps(props: Props, state: State) {
    return {
      ...state,
      expandedFromParent: props.expanded,
    };
  }

  componentDidMount() {
    this.setExpandedIds();
  }

  setExpandedIds = () => {
    // Root node is always expanded
    let expanded = this.state.expanded.concat(
      [this.props.tree.data.id],
      this.state.expandedFromParent,
    );
    if (this.props.expandAll) {
      expanded = this.state.expanded.concat(
        [this.props.tree.data.id],
        this.expandAllNodes(this.props.tree.children, []),
      );
    }
    this.setState({
      expanded,
    });
  };

  onMouseEnter = (id: number) => () => {
    this.setState({
      hoveredNode: id,
    });
  };

  onMouseLeave = () => {
    this.setState({
      hoveredNode: null,
    });
  };

  onClick = (branch: TreeBranch) => () => {
    this.props.onClick(branch);
  };

  expandAllNodes = (children: TreeBranch[], ids: Array<number>) => {
    if (!children) {
      return [];
    }
    if (!children.length) {
      return ids;
    }
    return [
      ...new Set(
        children
          .map(item => {
            return this.expandAllNodes(item.children, [...ids, item.data.id]);
          })
          .reduce((acc, item) => {
            return [...acc, ...item];
          }),
      ),
    ];
  };

  toggle = (id: number) => () => {
    let index = this.state.expanded.indexOf(id);

    if (index === -1) {
      this.setState({
        expanded: this.state.expanded.concat([id]),
      });
    } else {
      this.setState({
        expanded: this.state.expanded.filter((expanded: number) => expanded !== id),
      });
    }
  };

  isExcluded = (branchId: number) => this.props.excluded.indexOf(branchId) !== -1;

  isHovered = (branch: TreeBranch) => {
    if (!this.props.hoverEmployee && branch.data.type === "EMPLOYEE_POSITION") {
      return false;
    }
    return branch.data.id === this.state.hoveredNode;
  };

  isHidden = (branch: TreeBranch) =>
    this.props.hideEmployee && branch.data.type === "EMPLOYEE_POSITION";

  isInactive = (branch: TreeBranch) =>
    this.props.showDisabled &&
    branch.data.type === "EMPLOYEE_POSITION" &&
    !(branch.data || {}).isActive;

  getEmployeesCount = (branch: TreeBranch) => {
    const employees = branch.children.filter((child: TreeBranch) => child.data.employeeId);
    // $FlowFixMe
    const inactiveEmployees = employees.filter(employee => employee.data.isActive === false);
    return {
      total: employees,
      inactive: inactiveEmployees,
    };
  };

  hasChildren = (branch: TreeBranch) => {
    if (!this.props.showLeavers) {
      return (
        branch.children.filter(
          child =>
            child.data.type !== "EMPLOYEE_POSITION" ||
            (child.data.type === "EMPLOYEE_POSITION" && child.data.isActive),
        ).length > 0
      );
    }

    if (this.props.showEmployees) {
      return branch.children.length > 0;
    }

    return branch.children.filter(child => !child.data.employeeId).length > 0;
  };

  renderBranch = (branch: TreeBranch, expandedIds: number[], isRootNode: boolean = false) => {
    if (this.isExcluded(branch.data.id)) {
      return <div key={branch.data.id} />;
    }

    if (
      (!this.props.showEmployees && branch.data.employeeId) ||
      (!this.props.showLeavers && branch.data.type === "EMPLOYEE_POSITION" && !branch.data.isActive)
    ) {
      return null;
    }

    const isExpanded: boolean = expandedIds.indexOf(branch.data.id) !== -1;
    const isHovered: boolean = this.isHovered(branch);
    const controlComponent = this.props.renderControl(branch, isHovered, this.isInactive(branch));
    const isDraggable = this.props.draggable;
    const isSelected =
      branch.data.type === "ORGANISATIONAL_UNIT" && branch.data.id === this.props.selectedBranchId;

    return isDraggable && !this.props.disabled
      ? this.renderDraggableItem(
          branch,
          expandedIds,
          isExpanded,
          isSelected,
          controlComponent,
          isHovered,
        )
      : this.renderItem(branch, expandedIds, isExpanded, isSelected, controlComponent);
  };

  renderItem = (
    branch: TreeBranch,
    expandedIds: number[],
    isExpanded: boolean,
    isSelected: boolean,
    controlComponent: React$Node,
  ) =>
    !this.isHidden(branch) ? (
      <div
        key={branch.data.id}
        className={classNames("node", {
          expanded: isExpanded,
          "no-children": branch.children.length === 0,
        })}
      >
        <div
          className={classNames("node-name ellipsis", { selected: isSelected })}
          onClick={this.onClick(branch)}
        >
          {!this.props.disabled && this.hasChildren(branch) && (
            <span className="expand-collapse" onClick={this.toggle(branch.data.id)} />
          )}
          {this.props.disabled && <span className="dot" />}
          {!this.props.disabled && this.props.hasControl && (
            <div className="control">{controlComponent}</div>
          )}
          <span
            className={classNames("caption", {
              "text-negative": this.isInactive(branch),
            })}
            title={branch.data.label}
          >
            {branch.data.label}
          </span>
        </div>
        <div className="node-nodes">
          {isExpanded &&
            branch.children.map((childBranch: TreeBranch) =>
              this.renderBranch(childBranch, expandedIds),
            )}
        </div>
      </div>
    ) : null;

  renderDraggableItem = (
    branch: TreeBranch,
    expandedIds: number[],
    isExpanded: boolean,
    isSelected: boolean,
    controlComponent: React$Node,
    isHovered: boolean,
  ) => {
    const employees = this.getEmployeesCount(branch);
    return !this.isHidden(branch) ? (
      <DragWrapper
        className={classNames("w-100 node")}
        key={branch.data.path}
        name={branch.data.id}
        path={branch.data.path}
        label={branch.data.label}
        type={branch.data.type}
        onDrop={this.props.onDrop}
      >
        <div
          className={classNames({
            expanded: isExpanded,
            "no-children": branch.children.length === 0,
          })}
        >
          <DropWrapper
            className="w-100 drop-wrapper"
            name={branch.data.id}
            path={branch.data.path}
            label={branch.data.label}
            type={branch.data.type}
          >
            <div
              className={classNames("node-name ellipsis", {
                hovered: isHovered,
                selected: isSelected,
              })}
              onClick={this.onClick(branch)}
              onMouseEnter={this.onMouseEnter(branch.data.id)}
              onMouseLeave={this.onMouseLeave}
            >
              <div>
                {!this.props.disabled && branch.children.length > 0 && (
                  <span className="expand-collapse" onClick={this.toggle(branch.data.id)} />
                )}
                {this.props.disabled && <span className="dot" />}
                {!this.props.disabled && this.props.hasControl && (
                  <div
                    className={classNames("control", {
                      "can-drag": branch.data.type === "ORGANISATIONAL_UNIT",
                    })}
                  >
                    {controlComponent}
                  </div>
                )}
              </div>

              <ContextMenu clickTriggerEl={<span className="caption">{branch.data.label}</span>}>
                <MenuItem disableOnClick>
                  <View flex={1}>
                    {[
                      { key: "Name", value: branch.data.label },
                      { key: "Unit ID", value: branch.data.id },
                      { key: "Unit Ref", value: branch.data.reference },
                      {
                        key: "Employees",
                        value: (
                          <View direction="row" flex={1} gap={2}>
                            <Text text={employees.total.length} />
                            <span className="text-warning ellipsis">
                              ({employees.inactive.length})
                            </span>
                          </View>
                        ),
                      },
                    ].map(el => (
                      <View key={el.key} direction="row">
                        <Text className="flex-1" type="strong" text={el.key} />
                        <Text className="flex-1 ellipsis" type="span" text={el.value} />
                      </View>
                    ))}
                  </View>
                </MenuItem>
                {this.props.actions(branch)}
              </ContextMenu>
            </div>
          </DropWrapper>
          <div className="node-nodes">
            {isExpanded &&
              branch.children.map((childBranch: TreeBranch) =>
                this.renderBranch(childBranch, expandedIds),
              )}
          </div>
        </div>
      </DragWrapper>
    ) : null;
  };

  render() {
    return (
      <div className={classNames(this.props.className, "tree")}>
        {this.renderBranch(this.props.tree, this.state.expanded, true)}
      </div>
    );
  }
}

export default withDragDropContext(BaseHierarchyTree);
